import {inject, Injectable} from '@angular/core';
import {Manifest} from '../api/model/manifest';
import {ModuleManifest} from '../api/model/moduleManifest';
import {WorkflowService} from './workflow.service';
import {RulesetService} from './ruleset.service';
import {
  deriveRuleResults,
  describeRuleSteps,
  orderStepsByFlow,
  RuleResultItem,
} from '../components/workflow-editor/workflow.models';

/**
 * Nachgelagerte KI-Interpretation einer Hybrid-Prüfregel: Schritt-Texte und Ergebnis-Beschreibungen
 * naturalisieren und in einem zweiten, kleinen PUT persistieren.
 *
 * Warum ein Service und keine Komponentenmethode: `interpretRule` löst serverseitig ZWEI
 * sequenzielle LLM-Aufrufe aus (Timeout je 90 s). Seit der Workflow-Editor nach dem Übernehmen
 * sofort schließt, würde die Antwort auf einer bereits zerstörten Komponente landen. Hier läuft
 * sie unabhängig von der Lebensdauer der View zu Ende.
 *
 * Gearbeitet wird immer auf einem Schnappschuss des gespeicherten Manifests — nie auf dem
 * Live-Manifest eines noch offenen Editors, dessen zwischenzeitliche Änderungen der zweite PUT
 * sonst ungefragt mitspeichern würde.
 *
 * Best-effort: schlägt etwas fehl, ist die Regel trotzdem gespeichert und die Prüfregelübersicht
 * fällt auf Modulnamen bzw. die rohen Ergebnisse zurück.
 */
@Injectable({providedIn: 'root'})
export class RuleInterpretationService {
  private workflowService = inject(WorkflowService);
  private rulesetService = inject(RulesetService);

  /**
   * Interpretation für eine gespeicherte Regel anstoßen. Kehrt sofort zurück; der Aufrufer darf
   * danach wegnavigieren.
   *
   * @param manifest    Das gerade gespeicherte Manifest (wird intern kopiert).
   * @param definitionId Ziel der Nachpersistierung.
   * @param projectId   Projekt-Scope; im Admin-Modus ignoriert.
   * @param adminGlobal Admin-Pfad → projektlose globale Endpunkte.
   */
  interpretAndPersist(
    manifest: Manifest,
    modules: ModuleManifest[],
    definitionId: string,
    projectId: number,
    adminGlobal: boolean,
  ): void {
    if (!definitionId || definitionId === 'new') return;
    if ((manifest.steps?.length ?? 0) === 0) return;

    const snapshot: Manifest = JSON.parse(JSON.stringify(manifest));
    const stepDescriptors = describeRuleSteps(snapshot, modules);
    const rawResults = deriveRuleResults(snapshot);
    const resultDescriptors = rawResults.map(r => ({modul: r.label, details: r.description}));

    this.workflowService.interpretRule(stepDescriptors, resultDescriptors).subscribe({
      next: ({steps, results}) => {
        this.embedStepInterpretations(steps, snapshot);
        this.embedResultInterpretations(rawResults, results, snapshot);
        this.persist(snapshot, definitionId, projectId, adminGlobal);
      },
      error: err => console.error('Schritt-Interpretation fehlgeschlagen (Regel ist gespeichert):', err),
    });
  }

  /**
   * Zweiter, kleiner PUT nur für die Interpretationstexte — kein Toast, keine Navigation:
   * gespeichert ist zu diesem Zeitpunkt längst alles.
   */
  private persist(manifest: Manifest, definitionId: string, projectId: number, adminGlobal: boolean): void {
    const save$ = adminGlobal
      ? this.rulesetService.updateGlobalHybridDefinition(definitionId, manifest)
      : this.workflowService.updateDefinition(definitionId, manifest, projectId);
    save$.subscribe({
      error: err => console.error('Interpretationstexte konnten nicht gespeichert werden:', err),
    });
  }

  /**
   * Schritt-Texte in die Manifest-Schritte schreiben (leer → entfernen, damit die Übersicht auf den
   * Modulnamen zurückfällt). Nutzt dieselbe Flow-Reihenfolge wie `describeRuleSteps`, damit
   * `texts[i]` auf dem richtigen Schritt landet.
   */
  private embedStepInterpretations(texts: string[], manifest: Manifest): void {
    orderStepsByFlow(manifest).forEach((step, i) => {
      const text = (texts[i] ?? '').trim();
      const inputs = step.inputs ?? (step.inputs = {});
      if (text) {
        inputs['__aiInterpretation'] = text;
      } else {
        delete inputs['__aiInterpretation'];
      }
    });
  }

  /**
   * Die möglichen Ergebnisse der Regel (Label unverändert, Beschreibung KI-naturalisiert) in die
   * Trigger-Config schreiben, damit die Regelübersicht sie pro Regel anzeigen kann. Leer → entfernen.
   */
  private embedResultInterpretations(
    rawResults: RuleResultItem[],
    texts: string[],
    manifest: Manifest,
  ): void {
    if (!manifest.trigger) return;
    const cfg = manifest.trigger.config ?? (manifest.trigger.config = {});
    const items: RuleResultItem[] = rawResults.map((r, i) => ({
      label: r.label,
      description: (texts[i] ?? '').trim() || r.description,
      tone: r.tone,
    }));
    if (items.length) {
      cfg['__aiResults'] = items;
    } else {
      delete cfg['__aiResults'];
    }
  }
}
